/**
 * Canvas snapshot export (spec O10 / §14.4, client-side). Renders the current
 * canvas to PNG via cy.png() and stamps it with the Court-Safe state + a
 * provenance mark that BRANCHES BY SURFACE (FUSION_WORKBENCH §4, invariant #6):
 *   - demo (/fusion):        stamp `SYNTHETIC DATASET`
 *   - real (/cases/:id/canvas): stamp `case_id · court-safe state · date` (never SYNTHETIC)
 * A real-case export stamped SYNTHETIC — or a synthetic element inside one — is a bug,
 * so the caller must also DISABLE export while the demo overlay is on.
 *
 * Two outputs from one stamped render: a download (`exportCanvasSnapshot`), and
 * a case evidence item the case report shows under "Case canvas"
 * (`attachCanvasSnapshotToCase`, through POST /cases/{id}/evidence with
 * evidence_type `canvas_snapshot`; the PNG travels as a base64 data URL in the
 * payload with its SHA-256, the way notice PDFs are stored).
 */
import type cytoscape from 'cytoscape';

export type SnapshotMode = 'demo' | 'real';

export interface SnapshotOpts { mode: SnapshotMode; courtSafe: boolean; day: string; caseId?: string }

/** Evidence type the case report reads (backend `case_report.CANVAS_SNAPSHOT_EVIDENCE_TYPE`). */
export const CANVAS_SNAPSHOT_EVIDENCE_TYPE = 'canvas_snapshot';

/** Size cap for the attached PNG (the report embeds it; the backend refuses to embed above 8 MB). */
const ATTACH_MAX_SIDE = 2400;

/** Draw the PNG with the provenance footer; resolves to the stamped canvas. */
function stampSnapshot(png: string, opts: SnapshotOpts): Promise<HTMLCanvasElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onerror = () => reject(new Error('the canvas image could not be rendered'));
    img.onload = () => {
      const footer = 72;
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height + footer;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('no 2D drawing context')); return; }
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      ctx.fillStyle = opts.courtSafe ? '#065f46' : '#1e293b';
      ctx.fillRect(0, img.height, canvas.width, footer);

      ctx.textBaseline = 'middle';
      ctx.textAlign = 'left';
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 24px Inter, system-ui, sans-serif';
      ctx.fillText(
        opts.courtSafe ? 'Rendered in COURT-SAFE mode — deterministic evidence only'
                       : 'Full intelligence view — includes scored leads',
        24, img.height + footer / 2,
      );

      // Provenance stamp branches by surface — SYNTHETIC only for the demo.
      const rightStamp = opts.mode === 'demo'
        ? `SYNTHETIC DATASET · Fusion Canvas · ${opts.day}`
        : `Case ${opts.caseId ?? '—'} · ${opts.courtSafe ? 'court-safe' : 'full'} · ${opts.day}`;
      ctx.textAlign = 'right';
      ctx.fillStyle = '#cbd5e1';
      ctx.font = '16px Inter, system-ui, sans-serif';
      ctx.fillText(rightStamp, canvas.width - 24, img.height + footer / 2);
      resolve(canvas);
    };
    img.src = png;
  });
}

/** Download the stamped board. Resolves when the download was triggered (tests await it). */
export function exportCanvasSnapshot(cy: cytoscape.Core, opts: SnapshotOpts): Promise<void> {
  const png = cy.png({ full: true, scale: 2, bg: '#ffffff' });
  return stampSnapshot(png, opts).then((canvas) => {
    const tier = opts.courtSafe ? 'courtsafe' : 'full';
    const base = opts.mode === 'demo' ? 'case-canvas-demo' : `case-canvas-${opts.caseId ?? 'case'}`;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `${base}-${tier}-${opts.day}.png`;
    a.click();
  }).catch(() => { /* nothing to download */ });
}

/** SHA-256 (hex) of the bytes behind a base64 data URL. */
export async function sha256OfDataUrl(dataUrl: string): Promise<string> {
  const b64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

export interface SnapshotEvidenceInput {
  dataUrl: string;
  sha256: string;
  capturedAt: string;
  courtSafe: boolean;
  level?: 'low' | 'high' | string | null;
  nodeCount?: number | null;
  edgeCount?: number | null;
  width?: number | null;
  height?: number | null;
}

/** The POST /cases/{id}/evidence body for an attached snapshot. Pure. */
export function buildSnapshotEvidence(input: SnapshotEvidenceInput) {
  if (!/^data:image\/png;base64,/.test(input.dataUrl)) throw new Error('snapshot is not a PNG data URL');
  return {
    evidence_type: CANVAS_SNAPSHOT_EVIDENCE_TYPE,
    title: `Case canvas snapshot ${input.capturedAt.slice(0, 19).replace('T', ' ')} UTC`,
    description: "The officer's Case Canvas board as of the capture time, attached for the case report.",
    source: 'case_canvas',
    collected_at: input.capturedAt,
    hash_sha256: input.sha256,
    payload: {
      image_data_url: input.dataUrl,
      sha256: input.sha256,
      captured_at: input.capturedAt,
      court_safe: input.courtSafe,
      level: input.level ?? null,
      node_count: input.nodeCount ?? null,
      edge_count: input.edgeCount ?? null,
      width: input.width ?? null,
      height: input.height ?? null,
      mime: 'image/png',
    },
  };
}

type AddEvidence = (caseId: string, body: ReturnType<typeof buildSnapshotEvidence>) => Promise<unknown>;

/**
 * Render the stamped board and store it as a case evidence item. Real cases
 * only: a demo board is never attached to a case.
 */
export async function attachCanvasSnapshotToCase(
  cy: cytoscape.Core,
  opts: { caseId: string; courtSafe: boolean; level?: 'low' | 'high'; addEvidence: AddEvidence; now?: Date },
): Promise<unknown> {
  const now = opts.now ?? new Date();
  const capturedAt = now.toISOString();
  const png = cy.png({ full: true, scale: 2, bg: '#ffffff', maxWidth: ATTACH_MAX_SIDE, maxHeight: ATTACH_MAX_SIDE });
  const canvas = await stampSnapshot(png, { mode: 'real', courtSafe: opts.courtSafe, day: capturedAt.slice(0, 10), caseId: opts.caseId });
  const dataUrl = canvas.toDataURL('image/png');
  const sha256 = await sha256OfDataUrl(dataUrl);
  const visible = (sel: string) => cy.$(sel).filter((e) => e.visible()).length;
  const body = buildSnapshotEvidence({
    dataUrl, sha256, capturedAt, courtSafe: opts.courtSafe, level: opts.level ?? null,
    nodeCount: visible('node'), edgeCount: visible('edge'), width: canvas.width, height: canvas.height,
  });
  return opts.addEvidence(opts.caseId, body);
}
